import _ from 'lodash';
import { Store } from 'redux';
import ApiCalls from 'app/utils/apiCalls';
import { Study } from 'app/interfaces/Study';
import { recordRotation, StoreWithRotationHistory } from 'app/redux/rotationHistory/reducer';
import {
  getSpeciesAgnosticRegionKey,
  normalizeRotation,
  MAX_ROTATION_HISTORY_DEPTH,
} from 'app/utils/rotationHistory';
import logger from 'app/utils/debug/logger';
import isAcquisitionSoftware from 'app/utils/isAcquisitionSoftware';

const MIGRATION_FLAG_KEY = 'migration.rotationHistory.v1';
const TARGET_STUDY_COUNT = 200;
const MAX_PAGES = 10;

/**
 * Fetch studies page by page until we have at least TARGET_STUDY_COUNT studies
 * or there are no more pages.
 */
const fetchStudies = async (): Promise<Study[]> => {
  const allStudies: Study[] = [];

  for (let page = 1; page <= MAX_PAGES; page++) {
    const { data } = await ApiCalls.filterStudies({ page_number: page });
    const studies = data?.studies ?? [];
    if (studies.length === 0) break;

    allStudies.push(...studies);
    if (allStudies.length >= TARGET_STUDY_COUNT) break;
  }

  return allStudies.slice(0, TARGET_STUDY_COUNT);
};

/**
 * Extract rotation entries from studies: for each acquired image with an anatomic region
 * and a viewport rotation, produce a { regionKey, rotation } pair with the normalized rotation.
 */
const extractRotationEntries = (studies: Study[]): { regionKey: string; rotation: number }[] => {
  const entries: { regionKey: string; rotation: number }[] = [];

  for (const study of studies) {
    for (const image of study.images ?? []) {
      if (image.origin !== 'acquisition') continue;
      if (!image.anatomicRegion) continue;

      const regionKey = getSpeciesAgnosticRegionKey(image.anatomicRegion);
      const rotation = normalizeRotation(
        image.viewport?.rotation,
        image.viewport?.hflip,
        image.viewport?.vflip
      );

      entries.push({ regionKey, rotation });
    }
  }

  return entries;
};

/**
 * Group rotation entries by region key and keep only the last MAX_ROTATION_HISTORY_DEPTH
 * entries per region.
 */
const groupEntriesByRegion = (
  entries: { regionKey: string; rotation: number }[]
): Record<string, number[]> => {
  const grouped: Record<string, number[]> = {};

  for (const { regionKey, rotation } of entries) {
    if (!grouped[regionKey]) grouped[regionKey] = [];
    grouped[regionKey].push(rotation);
  }

  // Trim each region to the last MAX_ROTATION_HISTORY_DEPTH entries
  for (const key of Object.keys(grouped)) {
    if (grouped[key].length > MAX_ROTATION_HISTORY_DEPTH) {
      grouped[key] = grouped[key].slice(-MAX_ROTATION_HISTORY_DEPTH);
    }
  }

  return grouped;
};

/**
 * One-time migration that fetches the last ~100 studies from the API and
 * preloads the rotation history Redux state from their acquired images' viewports.
 *
 * Uses a localStorage flag to ensure it only runs once per browser.
 * Runs in the background (fire-and-forget) and does not block app startup.
 */
export const migrateRotationHistoryFromStudies = (store: Store<StoreWithRotationHistory>): void => {
  if (!isAcquisitionSoftware() || localStorage.getItem(MIGRATION_FLAG_KEY)) return;

  // Mark migration as started immediately to prevent concurrent runs
  localStorage.setItem(MIGRATION_FLAG_KEY, 'pending');

  fetchStudies()
    .then((studies) => {
      const entries = extractRotationEntries(studies);
      const grouped = groupEntriesByRegion(entries);

      for (const [regionKey, rotations] of Object.entries(grouped)) {
        for (const rotation of rotations) {
          store.dispatch(recordRotation({ regionKey, rotation }));
        }
      }

      localStorage.setItem(MIGRATION_FLAG_KEY, new Date().toISOString());
      logger.info(
        `Rotation history migration complete: ${entries.length} entries from ${studies.length} studies`
      );
    })
    .catch((error) => {
      // Remove the flag so migration can be retried on next startup
      localStorage.removeItem(MIGRATION_FLAG_KEY);
      logger.error('Rotation history migration failed', error);
    });
};

// Exported for testing
export { fetchStudies, extractRotationEntries, groupEntriesByRegion };
